import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import _ from 'lodash';
import React, { useCallback } from 'react';
import { Bar, getElementAtEvent } from 'react-chartjs-2';
import { useAppDispatch } from '../../app/hooks';
import { ScanDataEnum } from '../scanDataApi/scanDataDefinitions';
import { setHoverRowsideId } from '../select/selectSlice';
import { useEstimatedCountRowsideStackBarChart } from './hooks/useEstimatedCountRowsideStackBarChart';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  annotationPlugin
);

ChartJS.defaults.font.size = 16;
ChartJS.defaults.borderColor = '#7c7e80'; // darkTheme.grey.muted (set outside of theme context here)

interface Props {
  plugins?: object;
}

const BarChart: React.FC<Props> = ({ plugins }) => {
  const dispatch = useAppDispatch();
  const chartRef = React.useRef<ChartJS<'bar', number[], unknown> | null>(null);
  const { data, options } = useEstimatedCountRowsideStackBarChart(
    chartRef.current,
    plugins
  );

  const handleHover: React.MouseEventHandler<HTMLCanvasElement> = _.debounce(
    (event) => {
      if (!chartRef.current || !data?.rawData) return;

      const clickedEl = getElementAtEvent(chartRef.current, event);
      if (!clickedEl.length) {
        dispatch(setHoverRowsideId(''));
        return;
      }

      dispatch(
        setHoverRowsideId(
          data.rawData[clickedEl[0].index][ScanDataEnum.rowside_id]
        )
      );
    },
    200
  );

  const handleHoverEnd = useCallback(() => {
    dispatch(setHoverRowsideId(''));
  }, []);

  return (
    <>
      {data && (
        <Bar
          ref={chartRef}
          data={data}
          options={options}
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            minWidth: '300px',
            position: 'absolute', // this is needed to make the chart responsive - https://github.com/chartjs/Chart.js/issues/9001
          }}
          onMouseMove={handleHover}
          onMouseLeave={handleHoverEnd}
        />
      )}
    </>
  );
};

export default BarChart;
